// =============================================================================
// SIREPPAR - Banner de la alianza en el inicio del portal externo
// =============================================================================
// Es la puerta de entrada del partido al trámite de la alianza: en qué estado está,
// qué se espera de él, y la resolución cuando llega. Desde acá entra al expediente.
//
// Vale para las DOS agrupaciones: la alianza y cada partido integrante. El
// expediente es de todas ellas (ver `puedeAccederAlianza` en el backend), así que
// el banner ofrece lo mismo a todas; `tieneAcceso` sólo distingue si el usuario ES
// la alianza o la integra, para el texto.
//
// Un banner por alianza: un partido puede integrar más de una (convocatorias
// distintas).
//
// Generaliza el cartel de la Card F: cuando la alianza queda VIGENTE, este mismo
// componente es el que dice que el partido participa en ella y que los candidatos
// se presentan por la alianza. No son dos carteles que convivan.
// =============================================================================

import { Link } from 'react-router-dom';
import { documentoService, type AlianzaDelPartido } from '../../services/api';
import { useAlianzaPartido } from '../../hooks/useAlianzaPartido';
import { useAgrupaciones } from '../../hooks/useAgrupaciones';

// Mismos colores que la bandeja interna de alianzas, para que el estado se lea
// igual de los dos lados del sistema.
const COLOR_ESTADO: Record<string, string> = {
  PRESENTADO: 'bg-blue-100 text-blue-800',
  EN_ADMISIBILIDAD: 'bg-purple-100 text-purple-800',
  EN_ANALISIS: 'bg-yellow-100 text-yellow-800',
  OBSERVADO: 'bg-orange-100 text-orange-800',
  EN_SECRETARIO: 'bg-indigo-100 text-indigo-800',
  PENDIENTE_RESOLUCION: 'bg-indigo-100 text-indigo-800',
  EN_TEP: 'bg-cyan-100 text-cyan-800',
  NO_PRESENTADO: 'bg-red-100 text-red-800',
  VIGENTE: 'bg-green-100 text-green-800',
  RECHAZADO: 'bg-red-100 text-red-800',
};

const LABEL_ESTADO: Record<string, string> = {
  PRESENTADO: 'Presentada',
  EN_ADMISIBILIDAD: 'En admisibilidad',
  EN_ANALISIS: 'En análisis',
  OBSERVADO: 'Observada',
  EN_SECRETARIO: 'En el Secretario',
  PENDIENTE_RESOLUCION: 'En el Secretario',
  EN_TEP: 'En el TEP',
  // Se conserva sólo para los expedientes viejos que quedaron en ese estado: en
  // alianzas el circuito de "no presentada" ya no existe.
  NO_PRESENTADO: 'No presentada',
  VIGENTE: 'Reconocida',
  RECHAZADO: 'Rechazada',
};

/** Qué está esperando el partido en cada estado del trámite. */
function queSigue(estado: string): string {
  switch (estado) {
    case 'PRESENTADO':
    case 'EN_ADMISIBILIDAD':
      return 'La Secretaría Electoral está revisando la presentación. No hay nada que hacer hasta que se comunique.';
    case 'EN_ANALISIS':
      return 'La Secretaría Electoral está analizando la documentación. Si encuentra algo que corregir, va a observar el expediente.';
    case 'OBSERVADO':
      // El detalle y el plazo los da la card de alertas, que es donde se responde.
      // Repetirlos acá los mostraba dos veces en la misma pantalla.
      return 'La Secretaría Electoral observó la presentación. El detalle y el plazo para responder están en tus alertas.';
    case 'EN_SECRETARIO':
    case 'PENDIENTE_RESOLUCION':
      // El Secretario NO resuelve la constitución de alianza: la eleva al TEP, que
      // es quien dicta el acta. Decir acá que "resuelve" dejaba a las agrupaciones
      // esperando una decisión en una etapa que es sólo de paso.
      return 'El expediente está en el Secretario Electoral, que lo va a elevar al Tribunal Electoral Provincial. No hay nada que presentar en esta etapa.';
    case 'EN_TEP':
      return 'El expediente está a resolución del Tribunal Electoral Provincial. No hay nada que presentar en esta etapa.';
    case 'NO_PRESENTADO':
      return 'El plazo venció sin respuesta. El expediente quedó en manos del Secretario Electoral.';
    case 'VIGENTE':
      return 'La alianza está reconocida y habilitada para oficializar listas. Los candidatos se presentan por la alianza, no por cada partido.';
    case 'RECHAZADO':
      return 'El Tribunal Electoral Provincial rechazó la solicitud de constitución.';
    default:
      return 'Consultá el estado del trámite con la Secretaría Electoral.';
  }
}

const fmtFecha = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;

function BannerAlianza({ alianza }: { alianza: AlianzaDelPartido }) {
  const conAcceso = alianza.tieneAcceso;
  const estado = alianza.estadoExpediente;
  const resuelta = !!alianza.resolucion;

  // ¿El usuario puede ADMINISTRAR esta alianza, además de verla desde su partido?
  // Son dos cosas distintas: ver el expediente le alcanza con que su partido la
  // integre; administrarla requiere que la alianza figure entre sus agrupaciones.
  //
  // Y administrarla tiene UN SOLO fin: cargar la candidatura, que la presenta la
  // alianza y no cada partido. Por eso el botón no dice "administrar": dice para qué
  // sirve, y sólo aparece cuando la alianza está reconocida — antes de eso
  // Candidaturas no tiene nada que mostrarle (y la nav se la oculta igual, ver
  // TABS_ALIANZA_VIGENTE en MainLayout).
  const { agrupaciones, activa, cambiarA, cambiando } = useAgrupaciones();
  const puedeAdministrarla = agrupaciones.some(a => a.partidoId === alianza.alianzaId);
  const laEstaAdministrando = activa?.partidoId === alianza.alianzaId;
  // A dónde volver: sólo tiene sentido si la agrupación principal es OTRA. Cuando el
  // usuario se creó para la alianza (no tenía cuenta previa), su principal ES la
  // alianza y no hay ningún lugar al que volver — el botón decía "Volver a" el sitio
  // donde ya estaba.
  const principalDistinta = agrupaciones.find(a => a.esPrincipal && a.partidoId !== alianza.alianzaId) ?? null;
  const alianzaVigente = alianza.estadoExpediente === 'VIGENTE' || alianza.estadoAlianza === 'VIGENTE';

  return (
    <section className="bg-white shadow rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Alianza {alianza.denominacion}
              {alianza.sigla && <span className="text-gray-500 font-normal"> ({alianza.sigla})</span>}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {alianza.convocatoria?.denominacion || 'Sin convocatoria asociada'}
              {' · '}Expediente {alianza.expedienteNumero}
              {alianza.integrantes.length > 0 && (
                <> · {alianza.integrantes.length} {alianza.integrantes.length === 1 ? 'agrupación integrante' : 'agrupaciones integrantes'}</>
              )}
            </p>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
              COLOR_ESTADO[estado] || 'bg-gray-100 text-gray-800'
            }`}
          >
            {LABEL_ESTADO[estado] || estado}
          </span>
        </div>
      </div>

      <div className="px-4 py-4 sm:px-6 space-y-4">
        <p className="text-sm text-gray-700">{queSigue(estado)}</p>

        {/* Acta del TEP. Los archivos se descargan también sin acceso a la alianza:
            es el acto que resuelve y afecta directamente al partido. */}
        {alianza.resolucion && (
          <div
            className={`rounded-lg border p-3 ${
              alianza.resolucion.reconoce
                ? 'border-green-200 bg-green-50'
                : 'border-red-200 bg-red-50'
            }`}
          >
            <p
              className={`text-sm font-medium ${
                alianza.resolucion.reconoce ? 'text-green-900' : 'text-red-900'
              }`}
            >
              {alianza.resolucion.reconoce
                ? 'La alianza fue reconocida'
                : 'La solicitud de constitución fue rechazada'}
              {/* El número llega YA compuesto con el nombre del instrumento
                  ("ACTA N°001/2026", y "RESOLUCIÓN N°011/2025" en los expedientes
                  que resolvió el Secretario antes de este circuito). Anteponerle
                  otra vez la palabra duplicaba el rótulo y, peor, lo contradecía
                  cuando el instrumento no era el mismo. */}
              {alianza.resolucion.numero && ` — ${alianza.resolucion.numero}`}
              {alianza.resolucion.fecha && ` del ${fmtFecha(alianza.resolucion.fecha)}`}
            </p>
            {alianza.resolucion.archivos.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {alianza.resolucion.archivos.map(archivo => (
                  <button
                    key={archivo.id}
                    type="button"
                    onClick={() => documentoService.forceDownload(archivo.id, archivo.nombre)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {archivo.nombre}
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-sm text-gray-600">
                La resolución se envió por correo electrónico a las agrupaciones.
              </p>
            )}
          </div>
        )}

        {/* El integrante entra al MISMO expediente que la alianza. Se aclara que es
            compartido: si no, ver ahí la documentación de los otros partidos y las
            respuestas de otra agrupación parece un error del sistema. */}
        {!conAcceso && (
          <p className="text-sm text-gray-500">
            El expediente es de la alianza y lo comparten todos sus integrantes:
            cualquiera de ellos puede responder las observaciones.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {/* Es LA acción del cartel: el resto es informativo.
              DOS COMPORTAMIENTOS, y la diferencia no es un capricho:
              - Quien PUEDE administrar la alianza y todavía no la está
                administrando: se le cambia el contexto y se lo deja en el
                expediente. Antes abría el expediente siendo su propio partido, y
                entonces las acciones de la alianza no le aparecían: parecía que el
                sistema no lo dejaba hacer nada.
              - Quien sólo la INTEGRA: link común, sin cambio de contexto. No tiene
                membresía sobre la alianza, así que no hay contexto al que cambiar;
                el expediente se lo autoriza `agrupaciones_integrantes` (ver
                `expedienteController.getById`). Forzar el cambio acá lo dejaría
                afuera de su propio expediente. */}
          {puedeAdministrarla && !laEstaAdministrando ? (
            <button
              type="button"
              onClick={() => cambiarA(alianza.alianzaId, `/expedientes/${alianza.expedienteId}`)}
              disabled={cambiando}
              className="inline-flex items-center px-5 py-2.5 rounded-md bg-blue-600 hover:bg-blue-700 text-base font-semibold text-white transition disabled:opacity-50"
            >
              {cambiando ? 'Abriendo…' : 'Ver el expediente'}
            </button>
          ) : (
            <Link
              to={`/expedientes/${alianza.expedienteId}`}
              className="inline-flex items-center px-5 py-2.5 rounded-md bg-blue-600 hover:bg-blue-700 text-base font-semibold text-white transition"
            >
              Ver el expediente
            </Link>
          )}

          {/* Cargar candidatura. Cambia el contexto a la alianza y entra derecho a
              Candidaturas: el cambio de agrupación es el medio, no el objetivo, así
              que no se le pide al usuario que lo entienda como un paso aparte. */}
          {puedeAdministrarla && alianzaVigente && !laEstaAdministrando && (
            <button
              type="button"
              onClick={() => cambiarA(alianza.alianzaId, '/apoderado/panel')}
              disabled={cambiando}
              className="inline-flex items-center px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition disabled:opacity-60"
            >
              {cambiando ? 'Abriendo…' : 'Cargar candidatura'}
            </button>
          )}
          {/* Ya está trabajando como la alianza: no hace falta cambiar nada. */}
          {puedeAdministrarla && alianzaVigente && laEstaAdministrando && (
            <Link
              to="/apoderado/panel"
              className="inline-flex items-center px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition"
            >
              Cargar candidatura
            </Link>
          )}
          {laEstaAdministrando && principalDistinta && (
            <button
              type="button"
              onClick={() => cambiarA(principalDistinta.partidoId)}
              disabled={cambiando}
              className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-60"
            >
              {cambiando ? 'Cambiando…' : `Volver a ${principalDistinta.denominacion}`}
            </button>
          )}
          {/* Solicitar modificación de la alianza (Card H): genera un expediente
              nuevo. Todavía no está implementado, así que queda deshabilitado con el
              motivo a la vista en lugar de un link roto. */}
          {!resuelta && (
            <span
              className="inline-flex items-center px-3 py-2 rounded-md border border-gray-200 bg-gray-50 text-sm font-medium text-gray-400 cursor-not-allowed"
              title="Todavía no está disponible en el sistema"
            >
              Solicitar modificación de la alianza (no disponible)
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Todos los banners de alianza del partido logueado. El `id` es el destino de las
 * alertas del integrante sin acceso: su alerta linkea acá y no al expediente,
 * que le daría 403.
 */
export default function BannersAlianzas({ ocultarVigenteConAcceso = false }: { ocultarVigenteConAcceso?: boolean } = {}) {
  const { alianzas } = useAlianzaPartido();

  // Una alianza ya RECONOCIDA que el usuario administra no lleva banner cuando el
  // cartel de "¡Felicitaciones!" del inicio está en pantalla: los dos decían que la
  // alianza está vigente, y ese cartel ahora lleva la resolución.
  //
  // La condición la manda el llamador y NO se decide acá: el cartel de
  // felicitaciones depende de que la alianza esté habilitada en la convocatoria y de
  // que el usuario no lo haya cerrado. Decidiéndolo acá, una alianza vigente sin ese
  // cartel se quedaba sin banner y sin resolución en ningún lado — pasó de verdad.
  //
  // Al INTEGRANTE (`tieneAcceso: false`) siempre se le deja: es su única vía para
  // bajar la resolución desde el inicio, y él no ve el de felicitaciones porque
  // Candidaturas se habilita para la alianza, no para cada integrante.
  const visibles = ocultarVigenteConAcceso
    ? alianzas.filter((a) => !(a.tieneAcceso && (a.estadoExpediente === 'VIGENTE' || a.estadoAlianza === 'VIGENTE')))
    : alianzas;
  if (visibles.length === 0) return null;

  return (
    <div id="alianzas" className="space-y-4 scroll-mt-6">
      {visibles.map(alianza => (
        <BannerAlianza key={alianza.expedienteId} alianza={alianza} />
      ))}
    </div>
  );
}
